/**
 * Quick Actions Component
 * Provides shortcuts to common tasks
 */
import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';

interface QuickActionsProps {
  hasConnections: boolean;
}

interface ActionItem {
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  highlight?: boolean;
}

export default function QuickActions({ hasConnections }: QuickActionsProps) {
  const actions: ActionItem[] = hasConnections
    ? [
        {
          label: 'View Transactions',
          description: 'See all your transactions',
          href: '/transactions',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          ),
        },
        {
          label: 'See Flow',
          description: 'Visualize your money flow',
          href: '/flow',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
              />
            </svg>
          ),
        },
        {
          label: 'Categorize',
          description: 'Review uncategorized items',
          href: '/transactions?uncategorized=true',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
          ),
        },
        {
          label: 'Add Bank',
          description: 'Connect more accounts',
          href: '/connections',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
          ),
        },
      ]
    : [
        {
          label: 'Connect Your Bank',
          description: 'Get started by linking an account',
          href: '/connections',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          ),
          highlight: true,
        },
        {
          label: 'View Settings',
          description: 'Configure your account',
          href: '/settings',
          icon: (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          ),
        },
      ];

  return (
    <Card className="mt-6 lg:mt-0">
      <CardHeader title="Quick Actions" description="Common tasks" />
      <div className="grid grid-cols-2 gap-3">
        {actions.map((action) => (
          <Link
            key={action.href}
            to={action.href}
            className={`group flex flex-col items-center rounded-lg border p-4 text-center transition-colors hover:border-primary-300 hover:bg-primary-50 ${
              action.highlight
                ? 'border-primary-200 bg-primary-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                action.highlight
                  ? 'bg-primary-100 text-primary-600 group-hover:bg-primary-200'
                  : 'bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600'
              }`}
            >
              {action.icon}
            </div>
            <span className="mt-2 text-sm font-medium text-gray-900">{action.label}</span>
            <span className="mt-0.5 text-xs text-gray-500">{action.description}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}
